import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useData } from '../../context/DataContext';
import { v4 as uuidv4 } from 'uuid';
import RichTextEditor from '../common/RichTextEditor';
import { stripHtml } from '../../utils/stripHtml';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const ManageJobs = () => {
  const { state, dispatch } = useData();
  const { jobs, departments } = state;

  const [openDialog, setOpenDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    type: '',
    description: '',
    requirements: '',
    salary: '',
    showPay: true,
    isActive: true
  });

  // Use departments from database instead of hardcoded list
  const departmentOptions = departments && departments.length > 0 
    ? departments.map(dept => dept.name).sort()
    : [
        'Emergency Medicine', 'Cardiology', 'Neurology', 'Pediatrics', 'Orthopedics', 
        'Oncology', 'Laboratory', 'Nursing', 'Administration'
      ];
  const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Temporary', 'Internship'];

  const handleOpenDialog = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        title: job.title,
        department: job.department,
        type: job.type,
        description: job.description,
        requirements: job.requirements,
        salary: job.salary,
        showPay: job.showPay !== undefined ? job.showPay : true,
        isActive: job.isActive
      });
    } else {
      setEditingJob(null);
      setFormData({
        title: '',
        department: '',
        type: '',
        description: '',
        requirements: '',
        salary: '',
        showPay: true,
        isActive: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingJob(null);
    setFormData({
      title: '',
      department: '',
      type: '',
      description: '',
      requirements: '',
      salary: '',
      isActive: true
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSwitchChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked
    });
  };

  const handleBooleanChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked
    });
  };

  const handleSubmit = () => {
    if (editingJob) {
      // Update existing job
      dispatch({
        type: 'UPDATE_JOB',
        payload: {
          ...editingJob,
          ...formData
        }
      });
    } else {
      // Add new job
      const newJob = {
        id: uuidv4(),
        ...formData,
        postedDate: new Date().toISOString().split('T')[0]
      };
      dispatch({
        type: 'ADD_JOB',
        payload: newJob
      });
    }
    handleCloseDialog();
  };

  const handleDelete = (jobId) => {
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      dispatch({
        type: 'DELETE_JOB',
        payload: jobId
      });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Manage Job Postings
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add New Job
        </Button>
      </Box>

      {/* Jobs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Salary</TableCell>
              <TableCell>Show Pay</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Posted</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.id}>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {job.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {stripHtml(job.description).substring(0, 80)}...
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={job.department} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip label={job.type} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{job.salary}</TableCell>
                <TableCell>
                  <Chip 
                    label={job.showPay ? 'Yes' : 'No'} 
                    size="small" 
                    color={job.showPay ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={job.isActive ? 'Active' : 'Inactive'} 
                    size="small" 
                    color={job.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>{new Date(job.postedDate).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(job)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(job.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingJob ? 'Edit Job Posting' : 'Add New Job Posting'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  label="Department"
                  onChange={handleInputChange}
                  helperText={departments && departments.length > 0 
                    ? "Select from available departments" 
                    : "Loading departments..."}
                >
                  {departmentOptions.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Job Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  label="Job Type"
                  onChange={handleInputChange}
                >
                  {jobTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Salary Range (PKR)"
                name="salary"
                value={formData.salary}
                onChange={handleInputChange}
                placeholder="e.g., PKR 50,000 - PKR 70,000"
              />
            </Grid>
            <Grid item xs={12}>
              <RichTextEditor
                label="Job Description"
                value={formData.description}
                onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                placeholder="Describe the job responsibilities, duties, and expectations..."
                rows={4}
                helperText="Use the toolbar above to format your text with bold, italic, lists, and more"
              />
            </Grid>
            <Grid item xs={12}>
              <RichTextEditor
                label="Requirements"
                value={formData.requirements}
                onChange={(value) => setFormData(prev => ({ ...prev, requirements: value }))}
                placeholder="List the required qualifications, skills, and experience..."
                rows={3}
                helperText="Use the toolbar above to format your text with lists, bold, italic, etc."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showPay}
                    onChange={handleBooleanChange}
                    name="showPay"
                  />
                }
                label="Show Salary in Job Posting"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleSwitchChange}
                    name="isActive"
                  />
                }
                label="Active Job Posting"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingJob ? 'Update Job' : 'Add Job'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageJobs; 